import React from 'react';
import { Link } from 'react-router-dom';
import {
  Scan, HeartPulse, Activity, Clock, Shield, Truck, Zap,
  ChevronRight, Check, Phone, ArrowRight
} from 'lucide-react';

function Home() {
  return (
    <main>
      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-grid-pattern" />
          <div className="hero-float-orb orb-1" />
          <div className="hero-float-orb orb-2" />
        </div>

        <div className="container hero-content">
          <div className="hero-text">
            <div className="hero-badge fade-in">
              <span className="pulse-dot" />
              24/7 Portable Diagnostic Services
            </div>

            <h1 className="hero-title fade-in fade-in-delay-1">
              Portable Diagnostic Imaging,{' '}
              <span className="highlight">Delivered to You</span>
            </h1>

            <p className="hero-description fade-in fade-in-delay-2">
              High-quality mobile X-ray, ultrasound, and EKG services brought directly
              to your facility. Fast, accurate, and compassionate care — wherever you are
              in Los Angeles.
            </p>

            <div className="hero-actions fade-in fade-in-delay-3">
              <Link to="/contact" className="btn btn-primary">
                Order an Exam <ArrowRight size={18} />
              </Link>
              <a href="tel:8186265240" className="btn btn-outline">
                <Phone size={18} /> Call Now
              </a>
            </div>

            <div className="hero-stats fade-in fade-in-delay-4">
              <div className="hero-stat">
                <h3>24/7</h3>
                <p>Service Available</p>
              </div>
              <div className="hero-stat">
                <h3>LA</h3>
                <p>Area Coverage</p>
              </div>
              <div className="hero-stat">
                <h3>3+</h3>
                <p>Diagnostic Services</p>
              </div>
            </div>
          </div>

          <div className="hero-visual fade-in fade-in-delay-3">
            <div className="hero-card-stack">
              <div className="hero-card">
                <div className="hero-card-header">
                  <div className="hero-card-icon">
                    <Scan />
                  </div>
                  <div>
                    <h3>Our Services</h3>
                    <p>On-site portable diagnostic imaging</p>
                  </div>
                </div>
                <div className="hero-card-features">
                  <div className="hero-card-feature">
                    <div className="feature-check"><Check size={16} /></div>
                    <span>Mobile X-Ray Imaging</span>
                  </div>
                  <div className="hero-card-feature">
                    <div className="feature-check"><Check size={16} /></div>
                    <span>Portable Ultrasound</span>
                  </div>
                  <div className="hero-card-feature">
                    <div className="feature-check"><Check size={16} /></div>
                    <span>EKG / ECG Testing</span>
                  </div>
                  <div className="hero-card-feature">
                    <div className="feature-check"><Check size={16} /></div>
                    <span>Digital Image Quality</span>
                  </div>
                </div>
              </div>

              <div className="hero-floating-badge badge-top">
                <span className="badge-dot green" />
                Available Now
              </div>
              <div className="hero-floating-badge badge-bottom">
                <span className="badge-dot gold" />
                Most Insurance Accepted
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="services-section">
        <div className="container">
          <div className="services-header">
            <div className="section-label" style={{ justifyContent: 'center' }}>Our Services</div>
            <h2 className="section-title">Comprehensive Portable Diagnostic Solutions</h2>
            <p className="section-subtitle">
              State-of-the-art portable imaging equipment brought directly to your
              facility, ensuring fast and accurate diagnostics without patient transport.
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <Scan />
              </div>
              <h3>Mobile X-Ray</h3>
              <p>
                High-resolution digital X-ray imaging using the PowerMax 1260 system.
                120kV power with reduced exposure times for superior image quality and patient comfort.
              </p>
              <span className="service-tag">Digital Imaging</span>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <HeartPulse />
              </div>
              <h3>Portable Ultrasound</h3>
              <p>
                Non-invasive diagnostic ultrasound services delivered on-site.
                Real-time imaging for quick assessment and accurate diagnosis at your location.
              </p>
              <span className="service-tag">Non-Invasive</span>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Activity />
              </div>
              <h3>EKG / ECG</h3>
              <p>
                Comprehensive electrocardiogram testing to monitor heart health.
                Portable EKG equipment for rapid cardiac assessment wherever needed.
              </p>
              <span className="service-tag">Cardiac Care</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY PDI ===== */}
      <section className="why-section">
        <div className="container">
          <div className="why-content">
            <div>
              <div className="section-label">Why Choose PDI</div>
              <h2 className="section-title">Healthcare That Comes to You</h2>
              <p className="section-subtitle" style={{ marginBottom: 0 }}>
                We bring advanced diagnostic equipment directly to your facility,
                eliminating the need for patient transport and reducing wait times.
              </p>
            </div>

            <div className="why-features">
              <div className="why-feature">
                <div className="why-feature-num">
                  <Clock size={22} />
                </div>
                <div>
                  <h4>24/7 Availability</h4>
                  <p>Round-the-clock service ensures portable diagnostic imaging is always available when you need it most.</p>
                </div>
              </div>

              <div className="why-feature">
                <div className="why-feature-num">
                  <Zap size={22} />
                </div>
                <div>
                  <h4>Fast Turnaround</h4>
                  <p>Digital imaging technology delivers rapid results, enabling faster clinical decision-making.</p>
                </div>
              </div>

              <div className="why-feature">
                <div className="why-feature-num">
                  <Shield size={22} />
                </div>
                <div>
                  <h4>Insurance Friendly</h4>
                  <p>We accept most major insurance carriers and work with facilities to simplify the billing process.</p>
                </div>
              </div>

              <div className="why-feature">
                <div className="why-feature-num">
                  <Truck size={22} />
                </div>
                <div>
                  <h4>On-Site Convenience</h4>
                  <p>No need to transport patients. We bring state-of-the-art equipment directly to your location.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="section-label">Get Started Today</div>
            <h2 className="section-title">Ready to Order a Diagnostic Exam?</h2>
            <p className="section-subtitle">
              Our streamlined online system makes it easy to submit new exam orders.
              Just a few clicks to request mobile imaging services.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn cta-btn-dark">
                Order Exam Online <ChevronRight size={18} />
              </Link>
              <a href="tel:8186265240" className="btn cta-btn-light">
                <Phone size={18} /> (818) 626-5240
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
